import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MOCR_SECTIONS, MEMBERS, QUARTER_MONTHS, ATTESTATION_TEXT } from "../data/mocrQuestions";
import MocrPrintSheet from "./MocrPrintSheet";
import MocrCallMode from "./MocrCallMode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function SectionForm({ meta, record, onRecord }) {
  const stored = record.sections?.[meta.key] || { answers: {}, attestation: null };
  const [answers, setAnswers] = useState(stored.answers || {});
  const [attName, setAttName] = useState("");
  const [attSig, setAttSig] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState("");
  const dirtyRef = React.useRef(false);
  const closed = record.status === "CLOSED";
  const attestation = stored.attestation;

  useEffect(() => {
    setAnswers(record.sections?.[meta.key]?.answers || {});
    dirtyRef.current = false;
    setAutoState("");
  }, [record.month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave: answers register 1.2s after the last keystroke — no Save click needed
  useEffect(() => {
    if (!dirtyRef.current || closed) return;
    setAutoState("saving");
    const t = setTimeout(async () => {
      try {
        const res = await axios.put(`${API}/mocr/${record.month}/sections/${meta.key}`, { answers });
        onRecord(res.data);
        dirtyRef.current = false;
        setAutoState("saved");
      } catch {
        setAutoState("error");
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (qid, field, value) => {
    dirtyRef.current = true;
    setAnswers((prev) => ({ ...prev, [qid]: { answer: "", explanation: "", ...prev[qid], [field]: value } }));
  };

  const saveAnswers = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/mocr/${record.month}/sections/${meta.key}`, { answers });
      onRecord(res.data);
      toast.success(`Section ${meta.key} answers saved for ${record.month}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const attest = async () => {
    if (!attName.trim() || !attSig.trim()) {
      toast.error("Attestation requires name and typed signature");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/mocr/${record.month}/sections/${meta.key}`, {
        answers,
        attestation: { name: attName, signature: attSig },
      });
      onRecord(res.data);
      toast.success(`Section ${meta.key} attested`);
      setAttName("");
      setAttSig("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attestation failed");
    }
    setSaving(false);
  };

  return (
    <div className="mform" data-testid={`mocr-section-${meta.key}`}>
      <div className="mform-head">
        <span className="mform-title">{meta.title}</span>
        <span className="mform-lane">{meta.lane}
          {autoState === "saving" && <span className="autosave saving" data-testid={`autosave-${meta.key}`}> · saving…</span>}
          {autoState === "saved" && <span className="autosave saved" data-testid={`autosave-${meta.key}`}> · answers registered ✓</span>}
          {autoState === "error" && <span className="autosave error" data-testid={`autosave-${meta.key}`}> · save failed — retry</span>}
        </span>
      </div>
      <table className="mform-table">
        <tbody>
          <tr><th style={{ width: 52 }}>#</th><th>Question (answer for the review month)</th><th style={{ width: 190 }}>Answer</th><th style={{ width: 240 }}>Explanation — required for any No / non-zero / exception</th></tr>
          {meta.questions.map((q) => (
            <tr key={q.id}>
              <td className="q">{q.id}</td>
              <td>{q.text}</td>
              <td>
                <input className="inp" data-testid={`mocr-${q.id}-answer`} value={answers[q.id]?.answer || ""} disabled={closed}
                  onChange={(e) => setField(q.id, "answer", e.target.value)} placeholder="Yes / No / count…" />
              </td>
              <td>
                <textarea className="inp ta" rows={1} data-testid={`mocr-${q.id}-explanation`} value={answers[q.id]?.explanation || ""} disabled={closed}
                  onChange={(e) => setField(q.id, "explanation", e.target.value)} placeholder="—" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!closed && (
        <button className="btn ghost" onClick={saveAnswers} disabled={saving} data-testid={`mocr-save-${meta.key}`}>
          Save now (autosaves as you type)
        </button>
      )}
      <div className="attest-box">
        <div className="attest-text">ATTESTATION — {ATTESTATION_TEXT}</div>
        {attestation ? (
          <div className="attest-signed" data-testid={`mocr-attested-${meta.key}`}>
            SIGNED — {attestation.name} · “{attestation.signature}” · {fmtDate(attestation.date_signed)}
            {attestation.recorded_by ? <span className="att-acct"> · account: {attestation.recorded_by.name} ({attestation.recorded_by.email})</span> : null}
          </div>
        ) : closed ? (
          <div className="attest-signed warn">Not attested before close.</div>
        ) : (
          <div className="attest-row">
            <input className="inp" placeholder="Name" value={attName} onChange={(e) => setAttName(e.target.value)} data-testid={`mocr-attest-name-${meta.key}`} />
            <input className="inp sig" placeholder="Type name as signature" value={attSig} onChange={(e) => setAttSig(e.target.value)} data-testid={`mocr-attest-sig-${meta.key}`} />
            <button className="btn" onClick={attest} disabled={saving} data-testid={`mocr-attest-btn-${meta.key}`}>Sign &amp; attest Section {meta.key}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function QuarterlyBlock({ record, onRecord }) {
  const closed = record.status === "CLOSED";
  const existing = record.quarterly || [];
  const [sigs, setSigs] = useState({});
  const save = async () => {
    const attestations = MEMBERS.map((m) => {
      const prior = existing.find((x) => x.member === m);
      const typed = sigs[m];
      if (typed && typed.trim()) return { member: m, name: m.split("— ")[1] || m, signature: typed };
      return prior || null;
    }).filter(Boolean);
    if (!attestations.length) {
      toast.error("Type at least one member signature");
      return;
    }
    try {
      const res = await axios.put(`${API}/mocr/${record.month}/quarterly`, { attestations });
      onRecord(res.data);
      toast.success("Quarterly attestation saved");
      setSigs({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  return (
    <div className="mform quarterly" data-testid="mocr-quarterly">
      <div className="mform-head"><span className="mform-title">Quarterly Attestation (Mar / Jun / Sep / Dec only)</span>
        <span className="mform-lane">Each member confirms review of this MOCR and the quarter's findings.</span></div>
      {MEMBERS.map((m) => {
        const prior = (record.quarterly || []).find((x) => x.member === m);
        return (
          <div className="attest-row" key={m}>
            <span className="member-label">{m}</span>
            {prior ? (
              <span className="attest-signed">SIGNED — “{prior.signature}” · {fmtDate(prior.date_signed)}{prior.recorded_by ? <span className="att-acct"> · account: {prior.recorded_by.name}</span> : null}</span>
            ) : closed ? (
              <span className="attest-signed warn">Not signed</span>
            ) : (
              <input className="inp sig" placeholder="Type name as signature" value={sigs[m] || ""}
                onChange={(e) => setSigs((p) => ({ ...p, [m]: e.target.value }))} data-testid={`quarterly-sig-${m.slice(0, 4)}`} />
            )}
          </div>
        );
      })}
      {!closed && <button className="btn" onClick={save} data-testid="quarterly-save">Save quarterly attestation</button>}
    </div>
  );
}

export function MocrMonthlyForm() {
  const defaultMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1); // MOCR reviews the prior month
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [month, setMonth] = useState(defaultMonth());
  const [record, setRecord] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [callMode, setCallMode] = useState(false);

  const exportPdf = () => {
    setPrinting(true);
    document.body.classList.add("mocr-export");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("mocr-export");
      setPrinting(false);
    }, 250);
  };

  const load = useCallback(async (m) => {
    setRecord(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/mocr/${m}`);
      setRecord(res.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Could not load MOCR record");
    }
  }, []);

  useEffect(() => { load(month); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createMonth = async () => {
    try {
      const res = await axios.post(`${API}/mocr`, { month });
      setRecord(res.data);
      setNotFound(false);
      toast.success(`MOCR ${month} opened — issued day 1, responses due by the 10th`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open month");
    }
  };

  const closeMonth = async () => {
    try {
      const res = await axios.post(`${API}/mocr/${month}/close`);
      setRecord(res.data);
      toast.success(`MOCR ${month} closed — the pipeline report (SOP 14.1) may now issue`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot close");
    }
  };

  const isQuarter = QUARTER_MONTHS.includes(month.split("-")[1]);

  return (
    <div className="mocr-wrap">
      <div className="month-bar">
        <label>Review month</label>
        <input type="month" className="inp month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="mocr-month-input" />
        <button className="btn ghost" onClick={() => load(month)} data-testid="mocr-load-btn">Load</button>
        {record && (
          <span className={`status-pill ${record.status === "CLOSED" ? "closed" : "open"}`} data-testid="mocr-status">
            {record.status}{record.closed_at ? ` · ${fmtDate(record.closed_at)}` : ""}
          </span>
        )}
        {record?.status === "OPEN" && (
          <button className="btn" onClick={() => setCallMode(true)} data-testid="mocr-call-mode-btn" title="Screenshare view for the day-8 MOCR call: one section per screen, large type, every question requires an answer or a named action item (auto-logged to the CAP register).">
            ▶ Call mode (screenshare)
          </button>
        )}
        {record?.status === "CLOSED" && (
          <button className="btn" onClick={exportPdf} data-testid="mocr-export-pdf-btn" title="Opens the print dialog with the signed month formatted for the lender file — save as PDF.">
            Export signed PDF
          </button>
        )}
      </div>
      {notFound && (
        <div className="notfound">
          <p>No MOCR record exists for <b>{month}</b>. One record per month, never overwritten (workbook rule 1).</p>
          <button className="btn" onClick={createMonth} data-testid="mocr-create-btn">Open MOCR for {month}</button>
        </div>
      )}
      {record && (
        <>
          {callMode && record.status === "OPEN" && (
            <MocrCallMode record={record} onRecord={setRecord} onExit={() => setCallMode(false)} />
          )}
          {MOCR_SECTIONS.map((meta) => (
            <SectionForm key={meta.key + record.month} meta={meta} record={record} onRecord={setRecord} />
          ))}
          {isQuarter && <QuarterlyBlock record={record} onRecord={setRecord} />}
          {record.status === "OPEN" && (
            <div className="close-bar">
              <p className="close-note">Closing requires Sections A, B and C attested. Only then does the monthly pipeline report (ECH-SOP-14.1) issue to the noteholder.</p>
              <button className="btn danger" onClick={closeMonth} data-testid="mocr-close-btn">Close MOCR {month}</button>
            </div>
          )}
          {printing && <MocrPrintSheet record={record} />}
        </>
      )}
    </div>
  );
}

export function MocrHistoryLog() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/mocr`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  return (
    <div className="mocr-wrap" data-testid="mocr-history">
      <p className="lede" style={{ marginBottom: 10 }}>One row per month. The month record itself is the evidence; this log is the index (workbook HISTORY LOG). Retain 10 years.</p>
      <table>
        <tbody>
          <tr><th>Month</th><th>Status</th><th>Issued</th><th>A signed</th><th>B signed</th><th>C signed</th><th>Quarterly</th><th>MOCR closed</th></tr>
          {rows === null ? (
            <tr><td colSpan={8}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8}>No months on record yet — open the first one in the Monthly Form tab.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.month}>
                <td className="q">{r.month}</td>
                <td>{r.status}</td>
                <td>{fmtDate(r.issued_date)}</td>
                <td>{fmtDate(r.a_signed)}</td>
                <td>{fmtDate(r.b_signed)}</td>
                <td>{fmtDate(r.c_signed)}</td>
                <td>{r.quarterly_count ? `${r.quarterly_count}/3 signed` : "—"}</td>
                <td>{fmtDate(r.closed_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
